/*
================================================================ 
         Copyright (c) 2018-2020 Digitize Things Inc.           
                     All rights reserved.                       
================================================================ 
*/

import axios from "axios";
import store from "../store/store";
import constants from "./constants";
import AuthService from "../auth/authService";
import LogoutHelper from "./logoutHelper";

const BusApiCall = async (method, url, jsonData, pageOptions) => {
  return new Promise(async (resolve, reject) => {
    const state = store.getState();

    let headers = {};
    let tenantInfo = state.auth?.tenantInfo || {};
    let accessToken = state.auth?.access_token;

    // Tenant headers
    if (
      tenantInfo.tenantProviderType === "SALES_CHNL_PROVIDER" &&
      tenantInfo.tenantId
    ) {
      headers["x-tenant-id"] = tenantInfo.tenantId;
    } else if (
      tenantInfo.tenantProviderType === "SOLUTION_PROVIDER" &&
      jsonData?.tenantId
    ) {
      headers["x-assoc-tenant-id"] = jsonData.tenantId;
    }

    // Provider info
    if (
      tenantInfo.tenantProviderType &&
      tenantInfo.tenantProviderType !== "SALES_CHNL_PROVIDER"
    ) {
      headers["x-provider-id"] = tenantInfo.tenantProviderType;
      headers["x-tenant-id"] = tenantInfo.tenantId;
    }

    // Tokens
    headers["x-access-token"] = accessToken !== "null" ? accessToken : null;
    if (jsonData instanceof FormData) { } else {
      headers["Content-Type"] = "application/json";
    }
    headers["Accept"] = "application/json";
    headers["Client-App"] = "cumulator-webapp";
    if (tenantInfo.provider) headers["x-token-provider"] = tenantInfo.provider;

    const logout = () => LogoutHelper();

    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(";").shift();
      return null;
    };

    // Session validity
    if (accessToken) {
      const cookieToken = getCookie("access_token");
      if (!cookieToken) {
        logout();
        return;
      }
    }

    // Modernized error check
    const errorCheck = (error) => {
      console.log("Axios error captured:", error);

      // Use axios.isAxiosError for modern safety
      if (axios.isAxiosError(error)) {
        const msg = error.response?.data?.message || "";

        // Sanitize backend stacktrace leaks
        if (
          msg.includes("org.apache.ofbiz.entity") ||
          msg.includes("com.digitizethings.commission")
        ) {
          error.response.data.message = "Something went wrong";
        }

        // Handle common response codes
        if (error.response) {
          if (error.response.status === 401) {
            logout();
            reject(error);
          } else {
            reject(error);
          }
        } else if (error.code === "ERR_NETWORK") {
          console.warn("Network error — please check connection");
          reject(error);
        } else {
          reject(error);
        }
      } else {
        console.error("Non-Axios error caught:", error);
        reject(error);
      }
    };

    // Axios request execution (modern syntax)
    const requestConfig = { headers };

    if (pageOptions) {
      headers["x-batch-size"] = pageOptions.batchSize;
      headers["x-start-record"] = pageOptions.startRecord;
    }

    try {
      let response;
      switch (method.toUpperCase()) {
        case "GET":
          response = await axios.get(url, requestConfig);
          break;
        case "POST":
          response = await axios.post(url, jsonData, requestConfig);
          break;
        case "PUT":
          response = await axios.put(url, jsonData, requestConfig);
          break;
        case "PATCH":
          response = await axios.patch(url, jsonData, requestConfig);
          break;
        case "DELETE":
          response = await axios.delete(url, requestConfig);
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }

      resolve(response);
    } catch (error) {
      // Preserve your existing error flow
      if (error?.response?.status === 401) logout();
      errorCheck(error);
    }
  });
};

export default BusApiCall;
